import { Box, Button, Grid, Typography } from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useProductsContext } from "../../Contexts/SidebarContext";
import { NetworkManager } from "../../Components/NetworkManager";
import { CommonUtil, readJson } from "../../utils/CommonUtil";
import ToastManager from "../../Components/ToastManager";
import { useNavigate } from "react-router-dom";
import SearchBarWithButton from "../BroadcastFBManage/DMComponents/SearchBarWithButton";
import AudienceList from "../BroadcastFBManage/DMComponents/AudienceList";
import { CreateCustomAudienceDialog } from "./CreateCADialog";

interface Audience {
  customAudienceId: number;
  customAudienceName: string;
  customAudienceDesc: string;
  audienceSize: number;
  syncStatus: string;
}

const CustomAudiencePage: React.FC = () => {
  const navigate = useNavigate();
  const { sideBar } = useProductsContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [audience, setAudience] = useState<Audience[]>([]);

  const {
    facebook: { pageId },
  } = readJson("fbbPageDetail") || "{}";
  const { adAccountId } = readJson("fbbAdAccountDetail") || "{}";

  const filteredAudiences = useMemo(
    () =>
      audience.filter((a) =>
        a.customAudienceName?.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [audience, searchTerm],
  );
  const onSuccessCall = useCallback((data: string) => {
    const respData = CommonUtil.parseData(data);
    if (respData) {
      if (respData.statusCode === "BM0000" && respData.caDetailsList) {
        setAudience(respData.caDetailsList);
      } else {
        ToastManager.showToast("No custom audiences are available.", "Error");
      }
    }
  }, []);

  const onErrorCall = useCallback(() => {
    ToastManager.showToast("Failed to fetch custom audiences.", "Error");
  }, []);

  const fetchCustomAudiences = useCallback(() => {
    if (!pageId || !adAccountId) {
      return;
    }
    const pageDataReq = `pageId=${pageId}&adAccountId=${adAccountId}`;
    try {
      NetworkManager.sendJsonRequest(
        "broadcastCustomAudiences/getCustomAudiencesDetails",
        "",
        pageDataReq,
        "GET",
        true,
        onSuccessCall,
        onErrorCall,
      );
    } catch (error) {
      ToastManager.showToast("Error fetching custom audiences.", "Error");
    }
  }, [pageId, adAccountId, onSuccessCall, onErrorCall]);

  useEffect(() => {
    fetchCustomAudiences();
  }, [fetchCustomAudiences]);

  const handleCreate = useCallback(() => {
    setOpenDialog(true);
  }, []);

  const handleBackToPages = useCallback(() => {
    navigate("/existingbroadcastpages");
  }, [navigate]);

  return (
    <Box
      className="content-page"
      sx={{
        ml: sideBar ? "250px" : "120px",
        backgroundColor: "#fff",
        mr: 10,
      }}>
      <Box className="content">
        <Grid container spacing={2} mt={1}>
          <Grid item xs={4} mt={2}>
            <Button
              variant="outlined"
              sx={{ textTransform: "none", ml: 16 }}
              onClick={handleBackToPages}>
              ← Back To Pages
            </Button>
          </Grid>
          <Grid item xs={8} mt={2}>
            <Typography
              variant="h5"
              fontWeight="bold"
              gutterBottom
              sx={{ textAlign: "start", ml: 10 }}>
              Custom Audiences
            </Typography>
          </Grid>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={10} lg={8}>
              <SearchBarWithButton
                placeholder="Search Custom Audience..."
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onNew={handleCreate}
                newBtnLabel="+ Create Custom Audience"
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent="center"
            className="custom-scrollbar-css"
            sx={{ height: "60vh" }}>
            <Grid item xs={12} md={10} lg={8}>
              <AudienceList audiences={filteredAudiences} />
            </Grid>
          </Grid>
        </Grid>
        <CreateCustomAudienceDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSave={handleCreate}
          fetchCustomAudiences={fetchCustomAudiences}
        />
      </Box>
    </Box>
  );
};
export default CustomAudiencePage;
